import React from "react";
import { Card, Button, Input } from "components/ui";
import {
  ClockIcon,
  InformationCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { getUniqueFields, getCategoryConfig, getFieldIcon } from "../LeadDetails/DocumentsTab/utils/LeadUtils";
import { PencilIcon } from "@heroicons/react/24/solid";
import { canEditLead } from "permissions/permissionHelpers";
import { formatCostAustralian, isCostOrRevenueField } from "utils/formatCost";

/**
 * Main Component: Lead Details Form
 */
const LeadDetailsForm = ({
  isEditing,
  setIsEditing,
  loading,
  handleSave,
  leadData,
  onOpenQuickLeadModal,
  onOpenNotesModal,
  leadId,
  hideDescription = false
}) => {
  return (
    <div>
      {/* Header */}
      <div className="mb-2 flex items-center justify-end">
        {/* <Button
          onClick={() => setIsEditing(!isEditing)}
          variant={isEditing ? "outlined" : "soft"}
          color={isEditing ? "warning" : "primary"}
          className="flex items-center gap-2"
        >
          <PencilIcon className="h-4 w-4" />
          {isEditing ? "Cancel" : "Edit"}
        </Button> */}
        <div className="flex items-center gap-2">
          {onOpenNotesModal && (
            <Button
              onClick={() => onOpenNotesModal()}
              disabled={leadData?.IsStudent}
              variant="soft"
              color="success"
              className={`flex items-center gap-2 ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <PlusIcon className="h-4 w-4" />
              Add Note
            </Button>
          )}
          {canEditLead() && (
            <Button
              onClick={() => onOpenQuickLeadModal()}
              disabled={leadData?.IsStudent}
              variant="soft"
              color="primary"
              size="sm"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <PencilIcon className="h-3.5 w-3.5" />
              Edit
            </Button>
          )}
        </div>

      </div>

      <div className="space-y-5">
        <div className="space-y-5">
          {/* ========== Map Over LeadFieldCategories ========== */}
          {leadData?.LeadFieldCategories?.length > 0 ? (
            leadData.LeadFieldCategories.map((category) => {
              const config = getCategoryConfig(category.CategoryName);
              const Icon = config.icon;
              const uniqueFields = getUniqueFields(category.Fields);

              // Skip empty categories
              if (uniqueFields.length === 0) return null;

              return (
                <div key={category.CategoryId}>
                  {/* Category Header */}
                  <h3 className="text-[13px] dark:text-dark-50 mb-2.5 flex items-center gap-2 font-semibold text-gray-900 uppercase tracking-tight">
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full ${config.bgClass}`}
                    >
                      <Icon className={`h-4 w-4 ${config.iconClass}`} />
                    </span>
                    {category.CategoryName}
                  </h3>

                  {/* Fields Grid */}
                  <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-2">
                    {uniqueFields.map((field, index) => {
                      const FieldIcon = getFieldIcon(field.FieldName);
                      const isReferralSource = field.FieldName === "referral_source" || field.FieldLabel?.toLowerCase() === "referral source";
                      const rawValue = (isReferralSource && leadData?.SourceDetails?.ReferralSource)
                        ? leadData.SourceDetails.ReferralSource
                        : field.FieldValue;
                      const displayValue = (isCostOrRevenueField(field) && rawValue)
                        ? `$${formatCostAustralian(rawValue)}`
                        : rawValue;

                      return (
                        isEditing ? (
                          <Input
                            key={`${field.MasterFieldId}-${index}`}
                            label={field.FieldLabel}
                            value={displayValue || ""}
                            disabled={!isEditing}
                            prefix={<FieldIcon className="h-4 w-4" />}
                            description={
                              isEditing
                                ? `Edit ${field.FieldLabel.toLowerCase()}`
                                : ""
                            }
                          />
                        ) : (
                          <div
                            key={`${field.MasterFieldId}-${index}`}
                            className={`flex flex-col gap-0.5 px-0.5 min-w-0 ${(field.FieldLabel?.toLowerCase().includes("email") || field.FieldLabel?.toLowerCase().includes("mobile") || field.FieldLabel?.toLowerCase().includes("phone"))
                                ? "md:col-span-2"
                                : ""
                              }`}
                          >
                            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider dark:text-gray-400">
                              {field.FieldLabel}
                            </label>
                            <div className="flex items-start gap-2.5 text-[13px] font-medium text-gray-900 dark:text-dark-100 min-w-0">
                              <div className="flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 dark:bg-dark-700/50 shrink-0 mt-0.5">
                                <FieldIcon className="w-3.5 h-3.5 text-gray-400" />
                              </div>
                              <span className="truncate" title={displayValue}>{displayValue || "-"}</span>
                            </div>
                          </div>
                        )
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            // Empty State
            <Card className="dark:bg-dark-800/50 bg-gray-50 p-8 text-center">
              <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="dark:text-dark-300 mt-2 text-sm text-gray-500">
                No lead data available
              </p>
            </Card>
          )}

          {/* ========== Save / Cancel Panel ========== */}
          {isEditing && (
            <Card className="dark:bg-dark-800/50 border-dashed bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <div className="dark:text-dark-300 flex items-center gap-2 text-sm text-gray-600">
                  <ClockIcon className="h-4 w-4" />
                  <span>You have unsaved changes</span>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleSave}
                    variant="filled"
                    color="primary"
                    isGlow
                    disabled={loading || leadData?.IsStudent}
                    className="min-w-32"
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button
                    onClick={() => setIsEditing(false)}
                    variant="outlined"
                    color="neutral"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
};

export default LeadDetailsForm;
